// Detects critical findings in PEG (gastrostomy) feeding logs.
// Feeds the same WhatsApp critical alert used across the app.

export const METHOD_LABELS = {
  bolo: "Bolo (jeringa)",
  gravedad: "Gravedad",
  bomba: "Bomba de infusion",
};

export const TOLERANCE_LABELS = {
  bien: "Bien tolerada",
  nausea: "Nausea",
  vomito: "Vomito",
  regurgitacion: "Regurgitacion / reflujo",
  distension: "Distension abdominal",
  diarrea: "Diarrea",
};

export const STOMA_LABELS = {
  sano: "Sano",
  enrojecido: "Enrojecido",
  granuloma: "Granuloma",
  fuga: "Fuga periestomal",
  secrecion: "Secrecion serosa",
  secrecion_purulenta: "Secrecion purulenta",
  dolor: "Dolor",
};

export const PATENCY_LABELS = {
  permeable: "Permeable",
  lenta: "Pasa lento",
  obstruida: "Obstruida",
  salida_accidental: "Salida accidental",
};

// Gastric residual (ml) above this = alert (risk of intolerance / aspiration).
export const RESIDUAL_ALERT_ML = 200;

// Tolerances that imply bronchoaspiration risk -> immediate WhatsApp alert.
export const ASPIRATION_TOLERANCES = ["vomito", "regurgitacion"];

// Total water (flushes) for a single feeding row.
export function feedingWater(f) {
  return (Number(f?.flush_before_ml) || 0) + (Number(f?.flush_after_ml) || 0);
}

// Critical reasons for a single feeding row (used to build alert messages).
export function feedingCriticalReasons(f) {
  const reasons = [];
  if (f?.tolerance && ASPIRATION_TOLERANCES.includes(f.tolerance)) {
    reasons.push(
      `${TOLERANCE_LABELS[f.tolerance]} - riesgo de broncoaspiracion`
    );
  }
  const res = Number(f?.residual_ml) || 0;
  if (res > RESIDUAL_ALERT_ML) {
    reasons.push(`Residuo gastrico elevado (${res} ml, limite ${RESIDUAL_ALERT_ML} ml)`);
  }
  return reasons;
}

// Critical reasons for the tube/stoma care block.
export function tubeCareCriticalReasons(tc) {
  const reasons = [];
  if (!tc) return reasons;
  if (tc.patency === "obstruida") {
    reasons.push("Sonda obstruida - no pasa alimento ni agua");
  }
  if (tc.patency === "salida_accidental") {
    reasons.push("Salida accidental de la sonda - el estoma cierra en horas, urgente recolocar");
  }
  const stoma = Array.isArray(tc.stoma_status) ? tc.stoma_status : [];
  if (stoma.includes("secrecion_purulenta")) {
    reasons.push("Secrecion purulenta en estoma - posible infeccion");
  }
  if (stoma.includes("enrojecido") && stoma.includes("dolor")) {
    reasons.push("Estoma enrojecido y doloroso - vigilar infeccion");
  }
  return reasons;
}

// Human-readable critical reasons for the full gastrostomy data of a program.
// Used by supervisor-alert (WhatsApp) and by red banners in the UI.
export function detectGastrostomyCriticalReasons(g) {
  const reasons = [];
  const feedings = Array.isArray(g?.feedings) ? g.feedings : [];
  feedings.forEach((f) => {
    const label = f.time ? `${f.time}` : `#${f.number || "?"}`;
    feedingCriticalReasons(f).forEach((r) => reasons.push(`PEG ${label}: ${r}`));
  });
  tubeCareCriticalReasons(g?.tube_care).forEach((r) => reasons.push(`PEG sonda: ${r}`));
  return reasons;
}

// Aggregate totals for the shift (used in section footer and PDF).
export function summarizeGastrostomyShift(g) {
  const feedings = Array.isArray(g?.feedings) ? g.feedings : [];
  const formula = feedings.reduce((a, f) => a + (Number(f.volume_ml) || 0), 0);
  const water = feedings.reduce((a, f) => a + feedingWater(f), 0);
  const intolerance = feedings.filter(
    (f) => f.tolerance && f.tolerance !== "bien"
  ).length;
  return {
    count: feedings.length,
    formula_ml: formula,
    water_ml: water,
    total_ml: formula + water,
    intolerance_count: intolerance,
  };
}
